import { Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute, RouterOutlet } from '@angular/router';
import { UploadPolicy } from '@libraries/secure-uploader-client';
import { getUploadPolicy, isDesktop, leftColHidden } from '@libraries/ui-state';
import { Store, select } from '@ngrx/store';
import { ScrollToService } from '@nicky-lenaers/ngx-scroll-to';
import { Observable, Subscription } from 'rxjs';
import { takeWhile } from 'rxjs/operators';
import {
  fadeInAnimation,
  leftColAnimation,
  leftOutletTransitions,
  rightColAnimation,
  rightOutletTransitions,
} from '../../animations';

@Component({
  selector: 'base-layout',
  templateUrl: './base-layout.component.html',
  styleUrls: ['./base-layout.component.scss'],
  standalone: false,
  animations: [
    fadeInAnimation(),
    rightColAnimation,
    leftColAnimation,
    rightOutletTransitions,
    leftOutletTransitions,
  ],
})
export class BaseLayoutComponent implements OnDestroy {
  alive = true;
  isDesktop$ = this.store.pipe(select(isDesktop));
  leftColHidden$ = this.store.pipe(select(leftColHidden));
  _isDesktop = false;

  saveScroll = 0;
  onRight = false;
  firstLoad = true;
  hideLeft = false;
  hoaDocsContainer = false;

  @ViewChild('leftCol', { static: true }) leftCol!: ElementRef;
  @ViewChild('default', { static: true }) defaultOutlet!: RouterOutlet;
  @ViewChild('left', { static: true }) leftOutlet!: RouterOutlet;
  @ViewChild('right', { static: true }) rightOutlet!: RouterOutlet;
  uploadPolicy$!: Observable<UploadPolicy | undefined>;
  subscription!: Subscription;

  get rightIsPrimary() {
    return this.rightOutlet.activatedRouteData['primaryOutlet'] === 'right';
  }

  get isColumnLayout() {
    if (this.defaultOutlet.isActivated) {
      return false;
    }
    return this._isDesktop && this.leftOutlet.isActivated && this.rightOutlet.isActivated;
  }

  constructor(
    private store: Store,
    public route: ActivatedRoute,
    public scroller: ScrollToService
  ) {
    this.isDesktop$.pipe(takeWhile(() => this.alive)).subscribe((d) => (this._isDesktop = d));
    this.leftColHidden$.pipe(takeWhile(() => this.alive)).subscribe((h) => (this.hideLeft = h));
  }

  ngOnInit() {
    this.uploadPolicy$ = this.store.pipe(select(getUploadPolicy));
    this.subscription = this.uploadPolicy$.subscribe((data) => {
      if (data?.hoa_docs) {
        this.hoaDocsContainer = true;
      }
    });
  }

  ngOnDestroy() {
    this.alive = false;
    this.subscription?.unsubscribe();
  }

  adjustLeftScroll() {
    if (this.rightIsPrimary) {
      if (!this.onRight) this.saveScroll = window.pageYOffset;
      this.onRight = true;
      if (this._isDesktop && !this.firstLoad) {
        this.leftCol.nativeElement.style.top = '-' + this.saveScroll + 'px';
        setTimeout(() => {
          requestAnimationFrame(() => window.scrollTo(0, 0));
        }, 0);
      }
    } else {
      this.onRight = false;
      if (this._isDesktop && !this.firstLoad) {
        this.leftCol.nativeElement.style.top = '';
        window.scrollTo(0, this.saveScroll);
      }
    }
    this.firstLoad = false;
  }

  scrollTop() {
    setTimeout(
      () =>
        this.scroller.scrollTo({
          container: document.body,
          offset: 0,
          duration: 400,
        }),
      420
    );
  }

  showOverlay(onDesktop: boolean, primaryOutlet: string) {
    return (
      !this.rightOutlet.activatedRouteData['disableOverlay'] &&
      onDesktop &&
      primaryOutlet === 'right'
    );
  }

  leftState(primaryOutlet: string) {
    if (!primaryOutlet || !this._isDesktop || this.hideLeft) return '';
    return primaryOutlet === 'left' ? 'primary' : 'secondary';
  }

  rightState(primaryOutlet: string) {
    if (!primaryOutlet || !this._isDesktop || this.hideLeft) return '';
    return primaryOutlet === 'right' ? 'primary' : 'secondary';
  }

  outletAnimationState(outlet: RouterOutlet) {
    if (!outlet.isActivated) return '';
    return outlet.activatedRouteData.depth;
  }

  leftOutletTransitions(outlet: RouterOutlet) {
    if (!this._isDesktop || !outlet.isActivated || !outlet.activatedRouteData.step) return '';
    return outlet.activatedRouteData.step.toLowerCase();
  }
}
